import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Howl } from 'howler';
import { auth, db, functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import {
  doc, getDoc, setDoc, addDoc, collection, query, where,
  orderBy, getDocs, deleteDoc, serverTimestamp
} from 'firebase/firestore';
import { Card, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import {
  Play, Pause, SkipBack, SkipForward, ChevronLeft, ChevronRight,
  Volume2, VolumeX, Bookmark, BookmarkCheck, Download, List,
  X, Clock, Loader2, ArrowLeft, Moon, Cloud, Check, Trash2
} from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import {
  isNative, getChaptersOfflineStatus, saveChapterOffline,
  getCachedChapterUri, removeChapterOffline,
  saveProgressOffline, isOnline, onNetworkChange,
  getPendingProgress, clearPendingProgress
} from './lib/offlineStorage';
import {
  getRentalDownload, downloadRentalBundle, removeRentalDownload,
  readEncryptedChapterData
} from './lib/offlineRentalStore';
import { checkOfflineEntitlement, decryptChapterIfEntitled } from './lib/offlineRentalCrypto';
import { getFreeDiskBytes } from './lib/diskSpace';
import SpaceCheckDialog from './components/SpaceCheckDialog';
import {
  NativeAudioPlayer, shouldUseNativeAudio,
  updateMediaSession, clearMediaSession
} from './lib/nativeAudio';

/* ═══════════════════════════════════════════════════
   AUDIOBOOK PLAYER
   Full chapter-based audio player with bookmarks,
   progress sync, sleep timer, and speed controls.

   Audio: uses NativeAudioPlayer (HTML5 Audio) on
   Capacitor native for background playback + lock
   screen controls via Media Session API.
   Falls back to howler.js on web.
═══════════════════════════════════════════════════ */

const getAudioUrl = httpsCallable(functions, 'getAudioUrl');
const prepareOfflineBundle = httpsCallable(functions, 'prepareOfflineBundle');

// Free-space headroom (decimal, 500 MB) required BEYOND the book size for a
// download to proceed silently. Between book size and book size + headroom we warn;
// below book size we block. Comparisons use raw bytes; only display/thresholds are
// decimal to match Android's own Storage panel.
const SPACE_HEADROOM_BYTES = 500 * 1000 * 1000;

// Render a rental expiry as a short British-English date plus a days-left hint.
function formatRentalExpiry(expiresAtMs) {
  if (!expiresAtMs) return '';
  const date = new Date(expiresAtMs);
  const dateText = date.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
  const daysLeft = Math.ceil((expiresAtMs - Date.now()) / (24 * 60 * 60 * 1000));
  if (daysLeft <= 0) return `Expired ${dateText}`;
  if (daysLeft === 1) return `Expires tomorrow (${dateText})`;
  return `Expires in ${daysLeft} days (${dateText})`;
}

function formatTime(seconds) {
  if (!seconds || isNaN(seconds)) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatDuration(seconds) {
  if (!seconds || isNaN(seconds)) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m} min`;
}

const SPEED_OPTIONS = [0.75, 1, 1.25, 1.5, 2];
const SLEEP_OPTIONS = [
  { label: 'Off', value: 0 },
  { label: '15 min', value: 15 },
  { label: '30 min', value: 30 },
  { label: '45 min', value: 45 },
  { label: '60 min', value: 60 },
];

export default function AudiobookPlayer({ audiobook, onBack }) {
  const user = auth.currentUser;
  const audiobookId = audiobook?.id;
  const authorId = audiobook?.authorId;

  // Chapters fetched from Firestore subcollection
  const [chapters, setChapters] = useState([]);

  // Player state
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [chapterLoading, setChapterLoading] = useState(false);
  const [error, setError] = useState(null);

  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bookmarksOpen, setBookmarksOpen] = useState(false);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showSleepMenu, setShowSleepMenu] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);

  // Sleep timer
  const [sleepMinutes, setSleepMinutes] = useState(0);
  const [sleepRemaining, setSleepRemaining] = useState(0);

  // Bookmarks
  const [bookmarks, setBookmarks] = useState([]);
  const [bookmarkNote, setBookmarkNote] = useState('');
  const [showBookmarkForm, setShowBookmarkForm] = useState(false);

  // ─── Offline state (Capacitor native only) ───
  const [chapterOfflineStatus, setChapterOfflineStatus] = useState({}); // { chapterId: 'saved' | 'not_saved' | 'downloading' }
  const [isNativeApp] = useState(() => isNative());

  // ─── Offline rental state (Capacitor native only) ───
  // rentalDownload: the committed offline manifest (token, content key, chapters)
  // or null. Never logged. hasActiveRental: an unexpired rental exists for this
  // user + book (drives the "Download for offline" affordance even before download).
  const [rentalDownload, setRentalDownload] = useState(null);
  const [hasActiveRental, setHasActiveRental] = useState(false);
  const [rentalExpiresAt, setRentalExpiresAt] = useState(null);
  const [rentalDownloading, setRentalDownloading] = useState(false);
  const [rentalProgress, setRentalProgress] = useState(null); // { done, total } | null
  const [rentalExpired, setRentalExpired] = useState(false);
  // Free-space dialog state: { mode:'block'|'warn', bookTitle, neededBytes, freeBytes } | null
  const [spaceCheck, setSpaceCheck] = useState(null);
  // Honest partial-download dialog state: { done, total } | null
  const [partialDownload, setPartialDownload] = useState(null);

  // Refs
  const howlRef = useRef(null);
  const animFrameRef = useRef(null);
  const progressSaveRef = useRef(null);
  const sleepTimerRef = useRef(null);
  const seekBarRef = useRef(null);
  // Holds the in-memory blob: URL for the currently playing decrypted chapter, so
  // we can revoke it on chapter change / unmount. The decrypted audio only ever
  // lives behind this object URL — it is never written to disk.
  const offlineUrlRef = useRef(null);
  // Mirror of rentalDownload for the audio path, so loadChapterAudio never reads a
  // stale closure value (it can run during init, before state settles).
  const rentalDownloadRef = useRef(null);
  // Latest-callback ref for handleChapterEnd. The audio element's 'ended' listener
  // is wired once per player instance (which outlives the render that created it),
  // so calling handleChapterEnd directly would capture a STALE currentChapterIndex /
  // chapters / duration. Instead the listener calls chapterEndHandlerRef.current(),
  // which a no-deps effect keeps pointed at the current render's handleChapterEnd —
  // so auto-advance always reads fresh state.
  const chapterEndHandlerRef = useRef(null);
  // Holds a prepared bundle awaiting the user's "Download anyway" confirmation
  // from the almost-full warning, so we don't re-fetch it.
  const pendingBundleRef = useRef(null);

  // Apply a loaded offline-rental record to both the ref (audio path) and state (UI).
  const applyRentalRecord = useCallback((record) => {
    rentalDownloadRef.current = record;
    setRentalDownload(record);
    setRentalExpiresAt(record ? (record.expiresAt || null) : null);
    setRentalExpired(Boolean(record && record.expiresAt && record.expiresAt <= Date.now()));
  }, []);

  const currentChapter = chapters[currentChapterIndex];

  // ─── Fetch chapters + progress on mount ───
  useEffect(() => {
    if (!user || !audiobookId || !authorId) return;
    initPlayer();
    loadBookmarks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, audiobookId, authorId]);

  // ─── Native only: sync pending progress when coming back online ───
  useEffect(() => {
    if (!isNativeApp || !user) return;
    const unsubscribe = onNetworkChange(async (connected) => {
      if (connected) {
        await syncPendingProgress();
      }
    });
    // Also sync on mount in case we were offline last time
    syncPendingProgress();
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNativeApp, user]);

  // ─── Native only: load offline-rental status on mount ───
  // Looks for an existing offline download (works with no network) and, when
  // online, for an active unexpired rental so we can offer "Download for offline".
  useEffect(() => {
    if (!isNativeApp || !user || !audiobookId) return;
    let cancelled = false;
    (async () => {
      try {
        const record = await getRentalDownload(user.uid, audiobookId);
        if (!cancelled && record) applyRentalRecord(record);

        // Rental doc is online-only; skip it cleanly when offline.
        const online = await isOnline();
        if (!online) return;
        const rentalSnap = await getDoc(doc(db, 'audiobookRentals', `${user.uid}_${audiobookId}`));
        if (cancelled || !rentalSnap.exists()) return;
        const rental = rentalSnap.data();
        const expiresMs = rental.expiresAt?.toMillis ? rental.expiresAt.toMillis() : 0;
        if (rental.status === 'active' && expiresMs > Date.now()) {
          setHasActiveRental(true);
          setRentalExpiresAt(prev => prev || expiresMs);
        }
      } catch (err) {
        // Non-critical — a missing rental simply means no offline affordance.
        console.warn('[AudiobookPlayer] Could not load rental status');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNativeApp, user, audiobookId]);

  async function syncPendingProgress() {
    try {
      const pending = await getPendingProgress();
      if (pending.length === 0) return;
      const online = await isOnline();
      if (!online) return;
      for (const entry of pending) {
        if (entry.type === 'audiobook') {
          await setDoc(doc(db, 'audiobookProgress', entry.docId), {
            uid: entry.uid,
            audiobookId: entry.audiobookId,
            currentChapterId: entry.currentChapterId,
            positionSeconds: entry.positionSeconds,
            updatedAt: serverTimestamp(),
          }, { merge: true });
        }
      }
      await clearPendingProgress();
      console.log('[AudiobookPlayer] Synced offline progress');
    } catch (err) {
      console.warn('[AudiobookPlayer] Failed to sync offline progress:', err);
    }
  }

  async function initPlayer() {
    try {
      const online = isNativeApp ? await isOnline() : true;

      // Load the offline rental record up front (works offline, zero network) so the
      // very first chapter can take the decrypt path and so we can build the chapter
      // list from it when offline.
      let rentalRecord = null;
      if (isNativeApp && user) {
        rentalRecord = await getRentalDownload(user.uid, audiobookId);
        if (rentalRecord) applyRentalRecord(rentalRecord);
      }

      // 1. Chapter list: OFFLINE → from the local manifest (ZERO Firestore);
      //    ONLINE/web → the Firestore subcollection as before.
      let fetchedChapters = [];
      if (isNativeApp && !online && rentalRecord?.chapters?.length) {
        fetchedChapters = rentalRecord.chapters
          .map(c => ({ id: c.chapterId, title: c.title || 'Chapter', order: typeof c.order === 'number' ? c.order : 0 }))
          .sort((a, b) => a.order - b.order);
      } else {
        const chaptersRef = collection(db, `authors/${authorId}/audiobooks/${audiobookId}/chapters`);
        const chapSnap = await getDocs(query(chaptersRef, orderBy('order', 'asc')));
        fetchedChapters = chapSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Fallback: if no subcollection docs, check the audiobook doc for an embedded array
        if (fetchedChapters.length === 0 && audiobook?.chapters?.length > 0) {
          fetchedChapters = audiobook.chapters;
        }
      }

      if (fetchedChapters.length === 0) {
        setIsLoading(false);
        return;
      }

      setChapters(fetchedChapters);

      // Native only: load offline status for all chapters
      if (isNativeApp && user) {
        const chapterIds = fetchedChapters.map(c => c.id);
        const statuses = await getChaptersOfflineStatus(user.uid, chapterIds);
        setChapterOfflineStatus(statuses);
      }

      // 2. Saved progress — best-effort. OFFLINE → locally-saved pending progress;
      //    ONLINE → Firestore. Never throw on a failed read.
      let startChapterIdx = 0;
      let startPosition = 0;
      let progressData = null;
      if (isNativeApp && !online) {
        progressData = await getLocalProgress(user.uid, audiobookId);
      } else {
        try {
          const snap = await getDoc(doc(db, 'audiobookProgress', `${user.uid}_${audiobookId}`));
          if (snap.exists()) progressData = snap.data();
        } catch { progressData = null; }
      }
      if (progressData) {
        const savedIdx = fetchedChapters.findIndex(c => c.id === progressData.currentChapterId);
        if (savedIdx >= 0) {
          startChapterIdx = savedIdx;
          startPosition = progressData.positionSeconds || 0;
        }
      }

      setCurrentChapterIndex(startChapterIdx);
      setCurrentTime(startPosition);

      // 3. Load audio for the starting chapter
      await loadChapterAudio(fetchedChapters, startChapterIdx, startPosition);
      setIsLoading(false);
    } catch (err) {
      console.error('Failed to initialise player:', err);
      setError('Failed to load audiobook. Please try again.');
      setIsLoading(false);
    }
  }

  // Latest locally-saved progress for this book (written every 10s while offline),
  // or null. Pure local read — no network.
  async function getLocalProgress(uid, abId) {
    try {
      const pending = await getPendingProgress();
      const mine = pending.filter(p => p.type === 'audiobook' && p.uid === uid && p.audiobookId === abId);
      if (mine.length === 0) return null;
      const latest = mine[mine.length - 1];
      return { currentChapterId: latest.currentChapterId, positionSeconds: latest.positionSeconds };
    } catch { return null; }
  }

  async function loadBookmarks() {
    try {
      if (isNativeApp && !(await isOnline())) return; // zero Firestore offline
      const q = query(
        collection(db, 'audiobookBookmarks'),
        where('uid', '==', user.uid),
        where('audiobookId', '==', audiobookId),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      setBookmarks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Failed to load bookmarks:', err);
      setError('Could not load bookmarks. Please try again.');
    }
  }

  // ─── Load a chapter audio via Cloud Function ───
  // Accepts an explicit chaptersArr so it works during init (before state is set)
  async function loadChapterAudio(chaptersArr, chapterIndex, startPosition = 0, autoPlay = false) {
    const chapterList = chaptersArr || chapters;
    if (!chapterList[chapterIndex]) return;

    const chapter = chapterList[chapterIndex];
    setChapterLoading(true);
    setError(null);

    try {
      // Destroy previous howl
      if (howlRef.current) {
        howlRef.current.unload();
        howlRef.current = null;
      }
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }

      // Release any previous decrypted-chapter object URL (in-memory only).
      if (offlineUrlRef.current) {
        URL.revokeObjectURL(offlineUrlRef.current);
        offlineUrlRef.current = null;
      }

      let url = null;

      // Native + downloaded rental: verify the entitlement, then decrypt this
      // chapter in memory and play it. This takes priority over streaming so a
      // rented book plays with no network. Fail closed — nothing decrypts unless
      // the signed token verifies and we are still inside expiresAt.
      const rentalRecord = rentalDownloadRef.current;
      if (isNativeApp && rentalRecord &&
          rentalRecord.chapters?.some(c => c.chapterId === chapter.id)) {
        const ent = await checkOfflineEntitlement({
          token: rentalRecord.token,
          uid: user.uid,
          audiobookId,
        });
        if (!ent.ok) {
          setRentalExpired(ent.reason === 'expired');
          setError(ent.reason === 'expired'
            ? 'This rental has expired. Please renew or re-download to keep listening.'
            : 'This offline rental could not be verified. Please re-download.');
          setChapterLoading(false);
          return;
        }
        const chapterMeta = rentalRecord.chapters.find(c => c.chapterId === chapter.id);
        const data = await readEncryptedChapterData(user.uid, audiobookId, chapter.id);
        if (!data) throw new Error('Offline chapter is missing. Please re-download.');
        // Decrypt through the entitlement-bound wrapper: the upfront gate above
        // drives the UX, this re-checks as the structural backstop so decryption
        // can never run without a passing entitlement.
        const plaintext = await decryptChapterIfEntitled({
          token: rentalRecord.token,
          uid: user.uid,
          audiobookId,
          data,
          iv: chapterMeta.iv,
          contentKey: rentalRecord.contentKey,
        });
        // The decrypted audio lives only behind this blob URL — never on disk.
        const blob = new Blob([plaintext], { type: 'audio/mpeg' });
        url = URL.createObjectURL(blob);
        offlineUrlRef.current = url;
      }

      // Native only: check for a (non-rental) plaintext cached chapter.
      if (!url && isNativeApp) {
        const cachedUri = await getCachedChapterUri(user.uid, chapter.id);
        if (cachedUri) {
          url = Capacitor.convertFileSrc(cachedUri);
          console.log('[AudiobookPlayer] Playing from offline cache');
        }
      }

      // Native + offline + this chapter isn't in the downloaded set: streaming
      // would fail with an unhelpful generic error, so say the honest thing.
      if (!url && isNativeApp && !(await isOnline())) {
        setError(`Chapter ${chapterIndex + 1} isn't downloaded. Reconnect to stream or re-download to keep listening offline.`);
        setChapterLoading(false);
        return;
      }

      // Fall back to Cloud Function for streaming
      if (!url) {
        const result = await getAudioUrl({ audiobookId, chapterId: chapter.id });
        url = result.data?.url;
        if (!url) throw new Error('No audio URL returned');
      }

      // Use NativeAudioPlayer on Capacitor native, Howl on web
      if (shouldUseNativeAudio()) {
        return new Promise((resolve) => {
          const player = new NativeAudioPlayer({
            src: url,
            volume: isMuted ? 0 : volume,
            rate: playbackRate,
            onLoad: () => {
              setDuration(player.duration());
              if (startPosition > 0) {
                player.seek(startPosition);
                setCurrentTime(startPosition);
              }
              setChapterLoading(false);
              if (autoPlay) {
                player.play();
                setIsPlaying(true);
              }
              resolve();
            },
            onEnd: () => {
              chapterEndHandlerRef.current?.();
            },
            onLoadError: (err) => {
              console.error('Audio load error:', err);
              setError('Failed to load audio. Please try again.');
              setChapterLoading(false);
              resolve();
            },
            onPlayError: (err) => {
              console.error('Audio play error:', err);
              setError('Failed to play audio. Please try again.');
            },
          });
          howlRef.current = player;
        });
      } else {
        return new Promise((resolve) => {
          const howl = new Howl({
            src: [url],
            html5: true,
            volume: isMuted ? 0 : volume,
            rate: playbackRate,
            onload: () => {
              setDuration(howl.duration());
              if (startPosition > 0) {
                howl.seek(startPosition);
                setCurrentTime(startPosition);
              }
              setChapterLoading(false);
              if (autoPlay) {
                howl.play();
                setIsPlaying(true);
              }
              resolve();
            },
            onend: () => {
              chapterEndHandlerRef.current?.();
            },
            onloaderror: (_, err) => {
              console.error('Audio load error:', err);
              setError('Failed to load audio. Please try again.');
              setChapterLoading(false);
              resolve();
            },
            onplayerror: (_, err) => {
              console.error('Audio play error:', err);
              setError('Failed to play audio. Please try again.');
            },
          });
          howlRef.current = howl;
        });
      }
    } catch (err) {
      console.error('Failed to load chapter:', err);
      setError('Failed to load chapter. Please try again.');
      setChapterLoading(false);
    }
  }

  // ─── Time update loop ───
  useEffect(() => {
    function updateTime() {
      if (howlRef.current && howlRef.current.playing()) {
        const seek = howlRef.current.seek();
        if (typeof seek === 'number') {
          setCurrentTime(seek);
        }
      }
      animFrameRef.current = requestAnimationFrame(updateTime);
    }
    if (isPlaying) {
      animFrameRef.current = requestAnimationFrame(updateTime);
    }
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [isPlaying]);

  // ─── Save progress every 10 seconds ───
  useEffect(() => {
    if (!user || !audiobookId || chapters.length === 0) return;

    progressSaveRef.current = setInterval(() => {
      if (howlRef.current && isPlaying) {
        const seek = howlRef.current.seek();
        if (typeof seek === 'number') {
          saveProgress(currentChapterIndex, seek);
        }
      }
    }, 10000);

    return () => {
      if (progressSaveRef.current) clearInterval(progressSaveRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, audiobookId, currentChapterIndex, isPlaying]);

  async function saveProgress(chapterIdx, position) {
    try {
      const chapter = chapters[chapterIdx];
      if (!chapter) return;
      const progressDocId = `${user.uid}_${audiobookId}`;

      // Native only: if offline, save locally for later sync
      if (isNativeApp) {
        const online = await isOnline();
        if (!online) {
          await saveProgressOffline({
            type: 'audiobook',
            docId: progressDocId,
            uid: user.uid,
            audiobookId,
            currentChapterId: chapter.id,
            positionSeconds: position,
          });
          return;
        }
      }

      await setDoc(doc(db, 'audiobookProgress', progressDocId), {
        uid: user.uid,
        audiobookId,
        currentChapterId: chapter.id,
        positionSeconds: position,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch {
      // Non-critical — silently fail; on native try offline save
      if (isNativeApp) {
        const chapter = chapters[chapterIdx];
        if (chapter) {
          saveProgressOffline({
            type: 'audiobook',
            docId: `${user.uid}_${audiobookId}`,
            uid: user.uid,
            audiobookId,
            currentChapterId: chapter.id,
            positionSeconds: position,
          }).catch(() => {});
        }
      }
    }
  }

  // ─── Sleep timer ───
  useEffect(() => {
    if (sleepMinutes <= 0) {
      setSleepRemaining(0);
      if (sleepTimerRef.current) clearInterval(sleepTimerRef.current);
      return;
    }

    setSleepRemaining(sleepMinutes * 60);
    sleepTimerRef.current = setInterval(() => {
      setSleepRemaining(prev => {
        if (prev <= 1) {
          // Timer expired — pause playback
          if (howlRef.current && howlRef.current.playing()) {
            howlRef.current.pause();
            setIsPlaying(false);
          }
          setSleepMinutes(0);
          clearInterval(sleepTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (sleepTimerRef.current) clearInterval(sleepTimerRef.current);
    };
  }, [sleepMinutes]);

  // ─── Media Session — lock screen controls (native only) ───
  useEffect(() => {
    if (!shouldUseNativeAudio() || chapters.length === 0) return;

    updateMediaSession({
      title: currentChapter?.title || `Chapter ${currentChapterIndex + 1}`,
      artist: audiobook?.authorName || '',
      album: audiobook?.title || 'Audiobook',
      coverUrl: audiobook?.coverUrl || null,
      isPlaying,
      duration,
      position: currentTime,
      onPlay: () => {
        if (howlRef.current && !howlRef.current.playing()) {
          howlRef.current.play();
          setIsPlaying(true);
        }
      },
      onPause: () => {
        if (howlRef.current && howlRef.current.playing()) {
          howlRef.current.pause();
          setIsPlaying(false);
        }
      },
      onSeekBackward: () => skipBack(),
      onSeekForward: () => skipForward(),
      onPreviousTrack: currentChapterIndex > 0
        ? () => goToChapter(currentChapterIndex - 1, isPlaying)
        : null,
      onNextTrack: currentChapterIndex < chapters.length - 1
        ? () => goToChapter(currentChapterIndex + 1, isPlaying)
        : null,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, currentTime, duration, currentChapterIndex, chapters]);

  // ─── Cleanup on unmount ───
  useEffect(() => {
    return () => {
      if (howlRef.current) howlRef.current.unload();
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (progressSaveRef.current) clearInterval(progressSaveRef.current);
      if (sleepTimerRef.current) clearInterval(sleepTimerRef.current);
      if (offlineUrlRef.current) {
        URL.revokeObjectURL(offlineUrlRef.current);
        offlineUrlRef.current = null;
      }
      clearMediaSession();
    };
  }, []);

  // ─── Player controls ───
  function togglePlay() {
    if (!howlRef.current || chapterLoading) return;

    if (isPlaying) {
      howlRef.current.pause();
      setIsPlaying(false);
    } else {
      howlRef.current.play();
      setIsPlaying(true);
    }
  }

  function handleSeek(e) {
    if (!howlRef.current) return;
    const bar = seekBarRef.current;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const newTime = fraction * duration;
    howlRef.current.seek(newTime);
    setCurrentTime(newTime);
  }

  function skipBack() {
    if (!howlRef.current) return;
    const newTime = Math.max(0, (howlRef.current.seek() || 0) - 15);
    howlRef.current.seek(newTime);
    setCurrentTime(newTime);
  }

  function skipForward() {
    if (!howlRef.current) return;
    const current = howlRef.current.seek() || 0;
    const newTime = Math.min(duration, current + 15);
    howlRef.current.seek(newTime);
    setCurrentTime(newTime);
  }

  async function goToChapter(index, autoPlay = false) {
    if (index < 0 || index >= chapters.length) return;

    // Save current progress before switching
    if (howlRef.current) {
      const seek = howlRef.current.seek();
      if (typeof seek === 'number') {
        await saveProgress(currentChapterIndex, seek);
      }
      howlRef.current.stop();
    }
    // Do NOT force isPlaying false here — the onLoad callback sets the play state
    // from `autoPlay`. That lets auto-advance continue playing seamlessly and lets
    // manual navigation preserve the prior play state (autoPlay = wasPlaying).
    setCurrentChapterIndex(index);
    setCurrentTime(0);
    await loadChapterAudio(null, index, 0, autoPlay);
    // Save new chapter progress
    saveProgress(index, 0);
    setSidebarOpen(false);
  }

  function handleChapterEnd() {
    const nextIndex = currentChapterIndex + 1;
    if (nextIndex < chapters.length) {
      // Auto-advance: the user was already listening, so keep playing.
      goToChapter(nextIndex, true);
    } else {
      setIsPlaying(false);
      // Save completion
      saveProgress(currentChapterIndex, duration);
    }
  }

  // Keep the latest-callback ref pointed at the current render's handleChapterEnd so
  // the audio 'ended' listener (wired once per player, below) always invokes a fresh
  // closure — no stale currentChapterIndex / chapters / duration. Runs every commit.
  useEffect(() => {
    chapterEndHandlerRef.current = handleChapterEnd;
  });

  function changeSpeed(rate) {
    setPlaybackRate(rate);
    if (howlRef.current) {
      howlRef.current.rate(rate);
    }
    setShowSpeedMenu(false);
  }

  function changeVolume(e) {
    const val = parseFloat(e.target.value);
    setVolume(val);
    setIsMuted(false);
    if (howlRef.current) {
      howlRef.current.volume(val);
    }
  }

  function toggleMute() {
    if (isMuted) {
      setIsMuted(false);
      if (howlRef.current) howlRef.current.volume(volume);
    } else {
      setIsMuted(true);
      if (howlRef.current) howlRef.current.volume(0);
    }
  }

  // ─── Bookmarks ───
  async function addBookmark() {
    if (!currentChapter) return;
    const position = howlRef.current ? howlRef.current.seek() : currentTime;

    try {
      const bookmarkData = {
        uid: user.uid,
        audiobookId,
        chapterId: currentChapter.id,
        chapterTitle: currentChapter.title || `Chapter ${currentChapterIndex + 1}`,
        positionSeconds: typeof position === 'number' ? position : 0,
        note: bookmarkNote.trim().slice(0, 280),
        createdAt: serverTimestamp(),
      };

      await addDoc(collection(db, 'audiobookBookmarks'), bookmarkData);
      setBookmarkNote('');
      setShowBookmarkForm(false);
      await loadBookmarks();
    } catch (err) {
      console.error('Failed to save bookmark:', err);
      setError('Could not save bookmark. Please try again.');
    }
  }

  async function deleteBookmark(bookmarkId) {
    try {
      await deleteDoc(doc(db, 'audiobookBookmarks', bookmarkId));
      setBookmarks(prev => prev.filter(b => b.id !== bookmarkId));
    } catch (err) {
      console.error('Failed to delete bookmark:', err);
    }
  }

  function jumpToBookmark(bookmark) {
    const chapterIdx = chapters.findIndex(c => c.id === bookmark.chapterId);
    if (chapterIdx >= 0) {
      if (chapterIdx === currentChapterIndex) {
        // Same chapter — just seek
        if (howlRef.current) {
          howlRef.current.seek(bookmark.positionSeconds || 0);
          setCurrentTime(bookmark.positionSeconds || 0);
        }
      } else {
        setCurrentChapterIndex(chapterIdx);
        loadChapterAudio(null, chapterIdx, bookmark.positionSeconds || 0);
      }
    }
    setBookmarksOpen(false);
  }

  // ─── Download chapter (web only — native uses saveChapterForOffline instead) ───
  async function downloadChapter(chapter) {
    try {
      const result = await getAudioUrl({ audiobookId, chapterId: chapter.id });
      const url = result.data?.url;
      if (url) {
        if (Capacitor.isNativePlatform()) {
          const { Browser } = await import('@capacitor/browser');
          await Browser.open({ url });
        } else {
          window.open(url, '_blank');
        }
      }
    } catch (err) {
      console.error('Failed to get download URL:', err);
    }
  }

  // ─── Native only: save chapter for offline playback ───
  async function saveChapterForOffline(chapter) {
    if (!isNativeApp) return;
    try {
      setChapterOfflineStatus(prev => ({ ...prev, [chapter.id]: 'downloading' }));

      // Get signed URL from Cloud Function
      const result = await getAudioUrl({ audiobookId, chapterId: chapter.id });
      const url = result.data?.url;
      if (!url) throw new Error('No audio URL returned');

      // Download and save to Filesystem
      await saveChapterOffline(user.uid, chapter.id, url);
      setChapterOfflineStatus(prev => ({ ...prev, [chapter.id]: 'saved' }));
    } catch (err) {
      console.error('[AudiobookPlayer] Failed to save chapter offline:', err);
      setChapterOfflineStatus(prev => ({ ...prev, [chapter.id]: 'not_saved' }));
      setError('Failed to save chapter for offline. Please try again.');
    }
  }

  // ─── Native only: download the whole rented audiobook for offline ───
  // Calls prepareOfflineBundle (server verifies the active rental, hands back the
  // encrypted-chapter URLs, content key and signed token), then caches the
  // ciphertext and key material locally. Never logs the bundle (token / key).
  async function downloadRentalForOffline() {
    if (!isNativeApp || rentalDownloading) return;
    setError(null);
    setSpaceCheck(null);
    setPartialDownload(null);
    setRentalDownloading(true);
    setRentalProgress({ done: 0, total: chapters.length || 1 });

    const bundle = await prepareBundleOrNull();
    if (!bundle) {
      setRentalDownloading(false);
      setRentalProgress(null);
      return;
    }

    // Free-space pre-check — BEFORE any file writes (prepareOfflineBundle writes
    // nothing). Degrade open: if the book size or the free-disk figure is unknown
    // (web / plugin error / manifest seeded before totalBytes), skip the check and
    // download as before.
    const needed = typeof bundle.totalBytes === 'number' ? bundle.totalBytes : null;
    const disk = needed != null ? await getFreeDiskBytes() : null;
    if (needed != null && disk && typeof disk.freeBytes === 'number') {
      if (disk.freeBytes < needed) {
        setSpaceCheck({ mode: 'block', bookTitle: audiobook?.title || 'This audiobook', neededBytes: needed, freeBytes: disk.freeBytes });
        setRentalDownloading(false);
        setRentalProgress(null);
        return;
      }
      if (disk.freeBytes < needed + SPACE_HEADROOM_BYTES) {
        pendingBundleRef.current = bundle;
        setSpaceCheck({ mode: 'warn', bookTitle: audiobook?.title || 'This audiobook', neededBytes: needed, freeBytes: disk.freeBytes });
        setRentalDownloading(false);
        setRentalProgress(null);
        return;
      }
    }

    // performBundleDownload owns the downloading state + all error handling.
    await performBundleDownload(bundle);
  }

  // Continue a download the user confirmed from the almost-full warning. No error
  // handling of its own — performBundleDownload owns it.
  async function confirmDownloadAnyway() {
    const bundle = pendingBundleRef.current;
    pendingBundleRef.current = null;
    setSpaceCheck(null);
    if (bundle) await performBundleDownload(bundle);
  }

  // Prepare the offline bundle (the server verifies the rental). Returns the bundle,
  // or null after setting a user-facing error — so callers need no try/catch.
  async function prepareBundleOrNull() {
    try {
      const result = await prepareOfflineBundle({ audiobookId });
      const bundle = result.data;
      if (!bundle || !Array.isArray(bundle.chapters) || bundle.chapters.length === 0) {
        throw new Error('Nothing to download for this audiobook.');
      }
      return bundle;
    } catch (err) {
      // Log only the message — never the bundle, token or key.
      console.error('[AudiobookPlayer] Offline download failed:', err?.message || 'unknown error');
      setError(err?.code === 'permission-denied'
        ? 'Your rental is not active, so this audiobook cannot be downloaded.'
        : 'Could not download this audiobook for offline. Please try again.');
      return null;
    }
  }

  // Owns the whole download lifecycle: progress, local caching, error handling and
  // the downloading-state reset. Never throws. On an out-of-space failure the store
  // has committed a PARTIAL manifest, which we load so the downloaded chapters stay
  // playable, then surface the honest partial-state dialog.
  async function performBundleDownload(bundle) {
    setRentalDownloading(true);
    setRentalProgress({ done: 0, total: bundle.chapters.length });
    try {
      let partial = null; // { done, total } if the download ran out of space
      try {
        await downloadRentalBundle(user.uid, audiobookId, bundle, {
          onProgress: (p) => setRentalProgress(p),
          bookMeta: {
            title: audiobook?.title || null,
            authorName: audiobook?.authorName || null,
            authorId: audiobook?.authorId || authorId || null,
            coverUrl: audiobook?.coverUrl || null,
            chapters: chapters.map(c => ({ id: c.id, title: c.title || null, order: typeof c.order === 'number' ? c.order : null })),
          },
        });
      } catch (err) {
        if (err?.code !== 'no-space') throw err; // real failure → outer catch
        partial = { done: err.done, total: err.total };
      }
      // Full success OR a playable partial: load whatever the store committed.
      applyRentalRecord(await getRentalDownload(user.uid, audiobookId));
      setRentalExpired(false);
      if (partial) setPartialDownload(partial);
    } catch (err) {
      // Log only the message — never the bundle, token or key.
      console.error('[AudiobookPlayer] Offline download failed:', err?.message || 'unknown error');
      setError('Could not download this audiobook for offline. Please try again.');
    } finally {
      setRentalDownloading(false);
      setRentalProgress(null);
    }
  }

  // ─── Native only: remove the offline rental download ───
  // Deletes every local .enc file and all stored key material for this audiobook.
  async function removeRentalForOffline() {
    if (!isNativeApp) return;
    try {
      await removeRentalDownload(user.uid, audiobookId);
      if (offlineUrlRef.current) {
        URL.revokeObjectURL(offlineUrlRef.current);
        offlineUrlRef.current = null;
      }
      applyRentalRecord(null);
      setRentalExpired(false);
    } catch (err) {
      console.error('[AudiobookPlayer] Failed to remove offline download:', err?.message || 'unknown error');
    }
  }

  // ─── Native only: remove chapter from device ───
  async function removeChapterFromDevice(chapter) {
    if (!isNativeApp) return;
    try {
      await removeChapterOffline(user.uid, chapter.id);
      setChapterOfflineStatus(prev => ({ ...prev, [chapter.id]: 'not_saved' }));
    } catch (err) {
      console.error('[AudiobookPlayer] Failed to remove chapter:', err);
    }
  }

  // ─── Loading state ───
  if (isLoading) {
    return (
      <div className="listening-theme flex min-h-screen items-center justify-center bg-background text-foreground">
        <div className="text-center">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
          <p className="mt-4 text-sm text-muted-foreground">Loading audiobook...</p>
        </div>
      </div>
    );
  }

  if (chapters.length === 0) {
    return (
      <div className="listening-theme flex min-h-screen items-center justify-center bg-background text-foreground p-4">
        <Card className="w-full max-w-md border-border">
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">This audiobook has no chapters yet.</p>
            <Button onClick={onBack} variant="outline" className="mt-4 gap-1.5">
              <ArrowLeft className="h-4 w-4" /> Go back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const seekPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="listening-theme flex min-h-screen flex-col bg-background text-foreground">
      {/* ─── Header ─── */}
      <header className="sticky top-0 z-30 border-b border-border bg-card px-4 pt-[calc(0.75rem+env(safe-area-inset-top))] pb-3">
        <div className="mx-auto flex max-w-4xl items-center gap-3">
          <Button onClick={onBack} variant="ghost" size="sm" className="h-12 gap-1 text-muted-foreground">
            <ArrowLeft className="h-5 w-5" /> Back
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate font-display text-base font-semibold text-foreground">
              {audiobook?.title || 'Audiobook'}
            </h1>
            {audiobook?.authorName && (
              <p className="truncate text-xs text-muted-foreground">by {audiobook.authorName}</p>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              variant="ghost" size="sm"
              className="h-12 w-12 text-muted-foreground"
              title="Chapters"
            >
              <List className="h-5 w-5" />
            </Button>
            <Button
              onClick={() => setBookmarksOpen(!bookmarksOpen)}
              variant="ghost" size="sm"
              className="h-12 w-12 text-muted-foreground"
              title="Bookmarks"
            >
              <Bookmark className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* ─── Main content area ─── */}
      <div className="relative flex flex-1">
        {/* Chapter sidebar */}
        {sidebarOpen && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/30 md:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <aside className="fixed inset-y-0 left-0 z-50 w-80 overflow-y-auto border-r border-border bg-card pt-16 shadow-lg shadow-black/40 md:relative md:inset-auto md:z-auto md:pt-0 md:shadow-none">
              <div className="flex items-center justify-between border-b border-border px-4 py-3 md:hidden">
                <h2 className="text-sm font-semibold text-foreground">Chapters</h2>
                <Button onClick={() => setSidebarOpen(false)} variant="ghost" size="sm">
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="hidden border-b border-border px-4 py-3 md:block">
                <h2 className="text-sm font-semibold text-foreground">Chapters</h2>
              </div>
              <div className="divide-y divide-border">
                {chapters.map((ch, i) => {
                  const offlineState = chapterOfflineStatus[ch.id] || 'not_saved';
                  return (
                    <div
                      key={ch.id}
                      onClick={() => goToChapter(i, isPlaying)}
                      className={`flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-secondary ${
                        i === currentChapterIndex ? 'bg-accent/10 border-l-2 border-primary' : ''
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm truncate ${
                          i === currentChapterIndex ? 'font-semibold text-primary' : 'text-foreground'
                        }`}>
                          {ch.title || `Chapter ${i + 1}`}
                        </p>
                        {ch.duration && (
                          <p className="text-xs text-muted-foreground">{formatDuration(ch.duration)}</p>
                        )}
                      </div>

                      {/* Native only: offline download indicator */}
                      {isNativeApp ? (
                        offlineState === 'downloading' ? (
                          /* Downloading — spinner */
                          <div className="flex-shrink-0 p-1.5" title="Downloading...">
                            <Loader2 className="h-4 w-4 animate-spin text-primary" />
                          </div>
                        ) : offlineState === 'saved' ? (
                          /* Saved offline — tick, tap to remove */
                          <Button
                            onClick={(e) => { e.stopPropagation(); removeChapterFromDevice(ch); }}
                            variant="ghost" size="sm"
                            className="text-primary hover:text-destructive"
                            title="Saved offline — tap to remove"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        ) : (
                          /* Not saved — cloud icon, tap to save */
                          <Button
                            onClick={(e) => { e.stopPropagation(); saveChapterForOffline(ch); }}
                            variant="ghost" size="sm"
                            className="text-muted-foreground hover:text-primary"
                            title="Save for offline"
                          >
                            <Cloud className="h-4 w-4" />
                          </Button>
                        )
                      ) : (
                        /* Web: standard download button */
                        <Button
                          onClick={(e) => { e.stopPropagation(); downloadChapter(ch); }}
                          variant="ghost" size="sm"
                          className="text-muted-foreground hover:text-primary"
                          title="Download chapter"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </aside>
          </>
        )}

        {/* Bookmarks panel */}
        {bookmarksOpen && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/30 md:hidden"
              onClick={() => setBookmarksOpen(false)}
            />
            <aside className="fixed inset-y-0 right-0 z-50 w-80 overflow-y-auto border-l border-border bg-card pt-16 shadow-lg shadow-black/40 md:relative md:inset-auto md:z-auto md:pt-0 md:shadow-none">
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <h2 className="text-sm font-semibold text-foreground">Bookmarks</h2>
                <div className="flex items-center gap-1">
                  <Button
                    onClick={() => setShowBookmarkForm(!showBookmarkForm)}
                    variant="ghost" size="sm"
                    className="text-primary"
                    title="Add bookmark"
                  >
                    <BookmarkCheck className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={() => setBookmarksOpen(false)}
                    variant="ghost" size="sm"
                    className="md:hidden"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {showBookmarkForm && (
                <div className="border-b border-border p-4 space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Bookmark at {currentChapter?.title || 'current chapter'} — {formatTime(currentTime)}
                  </p>
                  <Textarea
                    value={bookmarkNote}
                    onChange={(e) => setBookmarkNote(e.target.value.slice(0, 280))}
                    placeholder="Add a note (optional)"
                    className="text-sm"
                    rows={2}
                  />
                  <p className="text-right text-xs text-muted-foreground">{bookmarkNote.length}/280</p>
                  <Button onClick={addBookmark} size="sm" className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                    Save bookmark
                  </Button>
                </div>
              )}

              <div className="divide-y divide-border">
                {bookmarks.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">No bookmarks yet</p>
                ) : (
                  bookmarks.map(bm => (
                    <div
                      key={bm.id}
                      className="flex items-start gap-2 px-4 py-3 cursor-pointer hover:bg-secondary transition-colors"
                      onClick={() => jumpToBookmark(bm)}
                    >
                      <Bookmark className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-primary" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground">
                          {bm.chapterTitle} — {formatTime(bm.positionSeconds)}
                        </p>
                        {bm.note && <p className="mt-0.5 text-xs text-muted-foreground">{bm.note}</p>}
                      </div>
                      <Button
                        onClick={(e) => { e.stopPropagation(); deleteBookmark(bm.id); }}
                        variant="ghost" size="sm"
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </aside>
          </>
        )}

        {/* Centre content — cover art and current chapter info */}
        <div className="flex flex-1 flex-col items-center justify-center p-6">
          {audiobook?.coverUrl && (
            <img
              src={audiobook.coverUrl}
              alt={audiobook.title || ''}
              className="mb-6 h-64 w-64 rounded-xl border border-border object-cover shadow-lg shadow-black/40 sm:h-72 sm:w-72"
            />
          )}
          <h2 className="font-display text-xl font-semibold text-foreground">
            {currentChapter?.title || `Chapter ${currentChapterIndex + 1}`}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Chapter {currentChapterIndex + 1} of {chapters.length}
          </p>

          {error && (
            <div className="mt-4 w-full max-w-md rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive-foreground">
              {error}
            </div>
          )}

          {sleepRemaining > 0 && (
            <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Moon className="h-3.5 w-3.5" />
              Sleep in {formatTime(sleepRemaining)}
            </div>
          )}

          {/* ─── Offline rental panel (native app only) ─── */}
          {isNativeApp && (hasActiveRental || rentalDownload) && (
            <div className="mt-6 w-full max-w-md rounded-xl border border-border bg-card p-4">
              {rentalExpiresAt && (
                <div className={`flex items-center gap-1.5 text-xs ${
                  rentalExpired ? 'text-foreground' : 'text-muted-foreground'
                }`}>
                  <Clock className="h-3.5 w-3.5" />
                  {formatRentalExpiry(rentalExpiresAt)}
                </div>
              )}

              {rentalExpired ? (
                <div className="mt-3 space-y-3">
                  <p className="text-sm text-foreground">
                    This rental has expired. Renew it to keep listening.
                  </p>
                  <Button
                    onClick={removeRentalForOffline}
                    variant="outline" size="sm"
                    className="gap-1.5 border-border text-muted-foreground"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Remove download
                  </Button>
                </div>
              ) : rentalDownload ? (
                <div className="mt-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-1.5 text-sm text-primary">
                    <Check className="h-4 w-4" /> Downloaded for offline
                  </div>
                  <Button
                    onClick={removeRentalForOffline}
                    variant="ghost" size="sm"
                    className="gap-1.5 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </Button>
                </div>
              ) : (
                <div className="mt-3">
                  <Button
                    onClick={downloadRentalForOffline}
                    disabled={rentalDownloading}
                    size="sm"
                    className="w-full gap-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
                  >
                    {rentalDownloading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {rentalProgress
                          ? `Downloading ${rentalProgress.done}/${rentalProgress.total}...`
                          : 'Downloading...'}
                      </>
                    ) : (
                      <><Download className="h-4 w-4" /> Download for offline</>
                    )}
                  </Button>
                  <p className="mt-2 text-center text-xs text-muted-foreground">
                    Listen with no connection until your rental expires.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ─── Fixed bottom player controls ─── */}
      <div className="sticky bottom-0 z-30 border-t border-border bg-card px-4 pb-[env(safe-area-inset-bottom)] shadow-[0_-2px_12px_rgba(0,0,0,0.4)]">
        <div className="mx-auto max-w-2xl py-3">
          {/* Seek bar */}
          <div className="mb-3">
            <div
              ref={seekBarRef}
              onClick={handleSeek}
              className="group relative h-2 cursor-pointer rounded-full bg-muted"
            >
              <div
                className="absolute left-0 top-0 h-full rounded-full bg-primary transition-all"
                style={{ width: `${seekPercent}%` }}
              />
              <div
                className="absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-primary shadow"
                style={{ left: `calc(${seekPercent}% - 8px)` }}
              />
            </div>
            <div className="mt-1 flex justify-between text-xs text-muted-foreground">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Controls — two deliberate rows (no wrap, identical structure at every width) */}
          <div className="flex flex-col gap-3">
            {/* Row 1: transport controls, centred */}
            <div className="flex items-center justify-center gap-1">
              <Button
                onClick={() => goToChapter(currentChapterIndex - 1, isPlaying)}
                disabled={currentChapterIndex === 0}
                variant="ghost" size="sm"
                className="h-12 w-12 text-muted-foreground disabled:opacity-30"
                title="Previous chapter"
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>

              <Button
                onClick={skipBack}
                variant="ghost" size="sm"
                className="h-12 w-12 px-0 text-muted-foreground"
                title="Skip back 15 seconds"
              >
                <SkipBack className="h-5 w-5" />
                <span className="text-[10px]">15</span>
              </Button>

              <Button
                onClick={togglePlay}
                disabled={chapterLoading}
                className="h-16 w-16 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {chapterLoading ? (
                  <Loader2 className="h-7 w-7 animate-spin" />
                ) : isPlaying ? (
                  <Pause className="h-7 w-7" />
                ) : (
                  <Play className="h-7 w-7 ml-0.5" />
                )}
              </Button>

              <Button
                onClick={skipForward}
                variant="ghost" size="sm"
                className="h-12 w-12 px-0 text-muted-foreground"
                title="Skip forward 15 seconds"
              >
                <span className="text-[10px]">15</span>
                <SkipForward className="h-5 w-5" />
              </Button>

              <Button
                onClick={() => goToChapter(currentChapterIndex + 1, isPlaying)}
                disabled={currentChapterIndex >= chapters.length - 1}
                variant="ghost" size="sm"
                className="h-12 w-12 text-muted-foreground disabled:opacity-30"
                title="Next chapter"
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            </div>

            {/* Row 2: secondary controls, evenly spread */}
            <div className="flex items-center justify-center gap-8">
              {/* Speed selector */}
              <div className="relative">
                <Button
                  onClick={() => { setShowSpeedMenu(!showSpeedMenu); setShowSleepMenu(false); }}
                  variant="ghost" size="sm"
                  className="h-12 min-w-[3rem] text-sm font-semibold text-muted-foreground"
                >
                  {playbackRate}x
                </Button>
                {showSpeedMenu && (
                  <div className="absolute bottom-full left-0 mb-1 rounded-lg border border-border bg-popover py-1 shadow-lg shadow-black/40">
                    {SPEED_OPTIONS.map(rate => (
                      <button
                        key={rate}
                        onClick={() => changeSpeed(rate)}
                        className={`block w-full px-4 py-1.5 text-left text-sm transition-colors hover:bg-secondary ${
                          rate === playbackRate ? 'font-semibold text-primary' : 'text-muted-foreground'
                        }`}
                      >
                        {rate}x
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Sleep timer */}
              <div className="relative">
                <Button
                  onClick={() => { setShowSleepMenu(!showSleepMenu); setShowSpeedMenu(false); }}
                  variant="ghost" size="sm"
                  className={`h-12 w-12 ${sleepMinutes > 0 ? 'text-primary' : 'text-muted-foreground'}`}
                  title="Sleep timer"
                >
                  <Clock className="h-5 w-5" />
                </Button>
                {showSleepMenu && (
                  <div className="absolute bottom-full left-0 mb-1 rounded-lg border border-border bg-popover py-1 shadow-lg shadow-black/40">
                    {SLEEP_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => { setSleepMinutes(opt.value); setShowSleepMenu(false); }}
                        className={`block w-full px-4 py-1.5 text-left text-sm transition-colors hover:bg-secondary ${
                          opt.value === sleepMinutes ? 'font-semibold text-primary' : 'text-muted-foreground'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Volume (desktop only) */}
              <div className="relative hidden sm:block">
                <Button
                  onClick={() => { setShowVolumeSlider(!showVolumeSlider); }}
                  variant="ghost" size="sm"
                  className="h-12 w-12 text-muted-foreground"
                  title={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted || volume === 0 ? (
                    <VolumeX className="h-5 w-5" onClick={toggleMute} />
                  ) : (
                    <Volume2 className="h-5 w-5" onClick={toggleMute} />
                  )}
                </Button>
                {showVolumeSlider && (
                  <div className="absolute bottom-full right-0 mb-1 rounded-lg border border-border bg-popover p-3 shadow-lg shadow-black/40">
                    <input
                      type="range"
                      min="0" max="1" step="0.05"
                      value={isMuted ? 0 : volume}
                      onChange={changeVolume}
                      className="h-1.5 w-24 cursor-pointer accent-primary"
                    />
                  </div>
                )}
              </div>

              {/* Bookmark */}
              <Button
                onClick={() => {
                  setShowBookmarkForm(true);
                  setBookmarksOpen(true);
                }}
                variant="ghost" size="sm"
                className="h-12 w-12 text-muted-foreground"
                title="Add bookmark"
              >
                <BookmarkCheck className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Free-space check (block / almost-full warning) */}
      <SpaceCheckDialog
        state={spaceCheck}
        onCancel={() => { setSpaceCheck(null); pendingBundleRef.current = null; }}
        onConfirm={confirmDownloadAnyway}
      />
      {/* Honest partial-download state after running out of space mid-download */}
      <SpaceCheckDialog
        state={partialDownload ? { mode: 'nospace', done: partialDownload.done, total: partialDownload.total } : null}
        onCancel={() => setPartialDownload(null)}
      />
    </div>
  );
}
